import {
  EntityAutocompletePicker,
  EntityFilter,
} from '@backstage/plugin-catalog-react';
import type { DefaultEntityFilters } from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import {
  getLocalDev,
  isLocalDevValue,
  localDevLabel,
  LOCAL_DEV_FIELDS,
  type LocalDevField,
} from './localDev';

/**
 * Filters the catalog on one or more `spec.localDev.<field>` values.
 *
 * The constructor takes a raw `string[]` so it can be used as the `Filter`
 * for {@link EntityAutocompletePicker} (which renders the same autocomplete
 * box as the built-in Tags / Owner filters). Invalid values are dropped so a
 * stale query parameter can't produce an impossible filter.
 */
export class LocalDevFieldFilter implements EntityFilter {
  // Typed as string[] to satisfy EntityAutocompletePicker's Filter contract;
  // only valid local-dev values ever make it in (see constructor).
  readonly values: string[];

  constructor(readonly field: LocalDevField, values: string[]) {
    this.values = values.filter(v => isLocalDevValue(field, v));
  }

  getCatalogFilters(): Record<string, string | string[]> {
    return { [`spec.localDev.${this.field}`]: this.values };
  }

  filterEntity(entity: Entity): boolean {
    const value = getLocalDev(entity, this.field);
    return !!value && this.values.includes(value);
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

type CustomFilters = DefaultEntityFilters & {
  localDevDocs?: LocalDevFieldFilter | undefined;
  localDevStatus?: LocalDevFieldFilter | undefined;
};

const FILTER_KEY = {
  docs: 'localDevDocs',
  status: 'localDevStatus',
} as const satisfies Record<LocalDevField, keyof CustomFilters>;

/**
 * Binds {@link LocalDevFieldFilter} to a specific field so its constructor
 * matches the `new (values: string[])` shape `EntityAutocompletePicker`
 * requires for its `Filter` prop.
 */
function makeFieldFilter(field: LocalDevField) {
  return class extends LocalDevFieldFilter {
    constructor(values: string[]) {
      super(field, values);
    }
  };
}

function LocalDevFieldPicker({ field }: { field: LocalDevField }) {
  return (
    <EntityAutocompletePicker<CustomFilters>
      label={localDevLabel(field)}
      name={FILTER_KEY[field]}
      path={`spec.localDev.${field}`}
      Filter={makeFieldFilter(field)}
    />
  );
}

/**
 * Sidebar filters for the two `spec.localDev` fields. Each picker hides
 * itself when the current entity list exposes no values for its field
 * (e.g. when viewing kinds other than Component), so no kind gating is
 * needed here.
 */
export function LocalDevPicker() {
  return (
    <>
      {LOCAL_DEV_FIELDS.map(field => (
        <LocalDevFieldPicker key={field} field={field} />
      ))}
    </>
  );
}
